import { useCallback, useEffect, useState } from 'react';
import {
  ArrowSquareOut,
  Briefcase,
  Cloud,
  Envelope,
  Globe,
  GraduationCap,
  Monitor,
  User,
  UserCircle,
  Warning,
} from '@phosphor-icons/react';
import * as RadixSwitch from '@radix-ui/react-switch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, Badge } from '@/components/ui';
import { cn } from '@/lib/utils/cn';
import type { PortfolioSectionKind } from '@/types/portfolio';
import { useAuth } from '@/lib/hooks/useAuth';
import { portfolioApi, type PortfolioState } from '@/lib/api/portfolio';
import { queryKeys } from '@/lib/api/queryKeys';
import { errorMessage } from '@/lib/api/errors';
import { PublishDialog } from './PublishDialog';
import { HeroEditor } from './sections/HeroEditor';
import { AboutEditor } from './sections/AboutEditor';
import { ProjectsEditor } from './sections/ProjectsEditor';
import { ExperienceEditor } from './sections/ExperienceEditor';
import { EducationEditor } from './sections/EducationEditor';
import { ContactEditor } from './sections/ContactEditor';
import { PreviewDrawer } from './preview/PreviewDrawer';
import type { PreviewDraft } from './preview/LivePreview';
import type {
  AboutValues,
  ContactValues,
  EducationValues,
  ExperienceValues,
  HeroValues,
  ProjectsValues,
} from './schemas';

interface SectionMeta {
  id: PortfolioSectionKind;
  label: string;
  hint: string;
  icon: React.ReactNode;
}

const SECTIONS: ReadonlyArray<SectionMeta> = [
  { id: 'hero', label: 'Hero', hint: 'Headline + portrait', icon: <UserCircle /> },
  { id: 'about', label: 'About', hint: 'Bio and skills', icon: <User /> },
  { id: 'projects', label: 'Projects', hint: 'Selected work', icon: <Briefcase /> },
  { id: 'experience', label: 'Experience', hint: 'Roles and tenure', icon: <Monitor /> },
  { id: 'education', label: 'Education', hint: 'Credentials', icon: <GraduationCap /> },
  { id: 'contact', label: 'Contact', hint: 'Email and links', icon: <Envelope /> },
];

type SaveState = 'saved' | 'dirty' | 'saving';

export function EditorPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [active, setActive] = useState<PortfolioSectionKind>('hero');
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [draft, setDraft] = useState<PreviewDraft>({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  const portfolioQuery = useQuery({
    queryKey: queryKeys.portfolio,
    queryFn: () => portfolioApi.get(),
    staleTime: 60_000,
  });
  const portfolio = portfolioQuery.data;
  const isPublished = Boolean(portfolio?.publishedAt);

  const enabledSet = new Set<PortfolioSectionKind>(
    portfolio?.enabledSections ?? ['hero', 'about', 'projects', 'experience', 'education', 'contact'],
  );

  useEffect(() => {
    if (portfolio?.draft) {
      setDraft(portfolio.draft);
    }
  }, [portfolio?.draft]);

  const settingsMutation = useMutation({
    mutationFn: (nextEnabled: PortfolioSectionKind[]) =>
      portfolioApi.updateSettings({ enabledSections: nextEnabled }),
    onSuccess: (next) => {
      qc.setQueryData<PortfolioState>(queryKeys.portfolio, next);
    },
    onError: (err) => {
      toast.error('Could not update sections', { description: errorMessage(err, 'Try again.') });
    },
  });

  function toggle(id: PortfolioSectionKind) {
    const currentEnabled = portfolio?.enabledSections ?? [
      'hero',
      'about',
      'projects',
      'experience',
      'education',
      'contact',
    ];
    const nextSet = new Set<PortfolioSectionKind>(currentEnabled);
    if (nextSet.has(id)) nextSet.delete(id);
    else nextSet.add(id);
    settingsMutation.mutate(Array.from(nextSet));
  }

  const sectionMutation = useMutation({
    mutationFn: async (args: { kind: PortfolioSectionKind; values: unknown }) =>
      portfolioApi.upsertSection(
        args.kind,
        args.values as NonNullable<PreviewDraft[PortfolioSectionKind]>,
      ),
    onMutate: () => setSaveState('saving'),
    onSuccess: (next) => {
      qc.setQueryData<PortfolioState>(queryKeys.portfolio, next);
      setSaveState('saved');
      toast.success('Section saved');
    },
    onError: (err) => {
      setSaveState('dirty');
      toast.error('Save failed', { description: errorMessage(err, 'Please try again.') });
    },
  });

  const onHeroDraft = useCallback(
    (v: unknown) => setDraft((d) => ({ ...d, hero: v as HeroValues })),
    [],
  );
  const onAboutDraft = useCallback(
    (v: unknown) => setDraft((d) => ({ ...d, about: v as AboutValues })),
    [],
  );
  const onProjectsDraft = useCallback(
    (v: unknown) => setDraft((d) => ({ ...d, projects: v as ProjectsValues })),
    [],
  );
  const onExperienceDraft = useCallback(
    (v: unknown) => setDraft((d) => ({ ...d, experience: v as ExperienceValues })),
    [],
  );
  const onEducationDraft = useCallback(
    (v: unknown) => setDraft((d) => ({ ...d, education: v as EducationValues })),
    [],
  );
  const onContactDraft = useCallback(
    (v: unknown) => setDraft((d) => ({ ...d, contact: v as ContactValues })),
    [],
  );

  const submitSection = useCallback(
    async (kind: PortfolioSectionKind, values: unknown) => {
      await sectionMutation.mutateAsync({ kind, values });
    },
    [sectionMutation],
  );

  return (
    <div className="-mx-10 -my-10 flex min-h-[calc(100vh-64px)]">
      <aside className="w-[280px] shrink-0 border-r border-line bg-board">
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-faint">
              Sections
            </p>
            <p className="mt-0.5 text-sm font-semibold text-ink">
              {enabledSet.size} of {SECTIONS.length} enabled
            </p>
          </div>
        </div>
        <nav className="p-3">
          {SECTIONS.map((s) => {
            const isActive = s.id === active;
            const isOn = enabledSet.has(s.id);
            return (
              <div
                key={s.id}
                className={cn(
                  'group mb-1 flex w-full items-center gap-3 rounded-lg pr-3 transition-colors',
                  isActive ? 'bg-rail' : 'hover:bg-rail/60',
                )}
              >
                <button
                  type="button"
                  onClick={() => setActive(s.id)}
                  className="flex flex-1 items-center gap-3 rounded-lg px-3 py-2.5 text-left"
                >
                  <span
                    className={cn(
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-line bg-board [&>*]:h-4 [&>*]:w-4',
                      isActive ? 'text-ink' : 'text-ink-muted',
                    )}
                  >
                    {s.icon}
                  </span>
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-ink">{s.label}</span>
                    <span className="block text-[11px] text-ink-faint">{s.hint}</span>
                  </span>
                </button>
                <RadixSwitch.Root
                  checked={isOn}
                  onCheckedChange={() => toggle(s.id)}
                  disabled={settingsMutation.isPending}
                  aria-label={`${isOn ? 'Disable' : 'Enable'} ${s.label}`}
                  className="relative h-5 w-9 shrink-0 rounded-full border border-line bg-rail transition-colors data-[state=checked]:border-ink data-[state=checked]:bg-ink"
                >
                  <RadixSwitch.Thumb className="absolute left-0 top-1/2 block h-[14px] w-[14px] -translate-y-1/2 translate-x-[2px] rounded-full bg-board shadow-sm transition-transform duration-150 data-[state=checked]:translate-x-[18px]" />
                </RadixSwitch.Root>
              </div>
            );
          })}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-line bg-board/70 px-10 py-4 backdrop-blur">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-faint">
              Editor · {active}
            </p>
            <h1 className="mt-1 font-display text-xl font-semibold tracking-tight">
              {SECTIONS.find((s) => s.id === active)?.label}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Badge tone={isPublished ? 'success' : 'soft'} leadingDot>
              {isPublished ? 'Published' : 'Draft'}
            </Badge>
            <SaveIndicator state={saveState} />
            <Button
              variant="secondary"
              size="md"
              trailingIcon={<ArrowSquareOut size={14} />}
              onClick={() => setPreviewOpen(true)}
            >
              Preview
            </Button>
            <Button
              variant="primary"
              size="md"
              leadingIcon={<Globe size={14} />}
              onClick={() => setPublishOpen(true)}
            >
              {isPublished ? 'Manage' : 'Publish'}
            </Button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-10 py-10">
          <div className="mx-auto max-w-2xl">
            {portfolioQuery.isPending ? (
              <div className="rounded-2xl border border-dashed border-line bg-board p-16 text-center text-sm text-ink-muted">
                Loading your portfolio…
              </div>
            ) : (
              <SectionEditor
                key={`${active}-${portfolio?.updatedAt ?? 'init'}`}
                section={active}
                portfolio={portfolio}
                enabled={enabledSet.has(active)}
                submitting={sectionMutation.isPending}
                onDirty={() => setSaveState('dirty')}
                onSubmit={(v) => submitSection(active, v)}
                onDraftChange={{
                  hero: onHeroDraft,
                  about: onAboutDraft,
                  projects: onProjectsDraft,
                  experience: onExperienceDraft,
                  education: onEducationDraft,
                  contact: onContactDraft,
                }}
              />
            )}
          </div>
        </div>
      </div>

      <PreviewDrawer
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        draft={draft}
        enabled={enabledSet}
        ownerName={user?.name ?? 'You'}
        template={portfolio?.template ?? 'minimal'}
      />

      <PublishDialog open={publishOpen} onOpenChange={setPublishOpen} portfolio={portfolio} />
    </div>
  );
}

function SectionEditor(props: {
  section: PortfolioSectionKind;
  portfolio: PortfolioState | undefined;
  enabled: boolean;
  submitting: boolean;
  onDirty: () => void;
  onSubmit: (values: unknown) => Promise<void>;
  onDraftChange: Record<PortfolioSectionKind, (v: unknown) => void>;
}) {
  if (!props.enabled) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-line bg-board p-16 text-center">
        <Warning size={24} className="text-ink-faint" />
        <h2 className="font-display text-xl font-semibold">Section disabled</h2>
        <p className="max-w-sm text-sm text-ink-muted">
          Toggle this section on in the sidebar to edit its content. Content is preserved when
          disabled.
        </p>
      </div>
    );
  }
  const draftFor = props.portfolio?.draft;
  switch (props.section) {
    case 'hero':
      return (
        <HeroEditor
          initial={draftFor?.hero}
          onDirty={props.onDirty}
          onSubmit={(v) => props.onSubmit(v)}
          submitting={props.submitting}
          onDraftChange={props.onDraftChange.hero}
        />
      );
    case 'about':
      return (
        <AboutEditor
          initial={draftFor?.about}
          onDirty={props.onDirty}
          onSubmit={(v) => props.onSubmit(v)}
          submitting={props.submitting}
          onDraftChange={props.onDraftChange.about}
        />
      );
    case 'projects':
      return (
        <ProjectsEditor
          initial={draftFor?.projects}
          onDirty={props.onDirty}
          onSubmit={(v) => props.onSubmit(v)}
          submitting={props.submitting}
          onDraftChange={props.onDraftChange.projects}
        />
      );
    case 'experience':
      return (
        <ExperienceEditor
          initial={draftFor?.experience}
          onDirty={props.onDirty}
          onSubmit={(v) => props.onSubmit(v)}
          submitting={props.submitting}
          onDraftChange={props.onDraftChange.experience}
        />
      );
    case 'education':
      return (
        <EducationEditor
          initial={draftFor?.education}
          onDirty={props.onDirty}
          onSubmit={(v) => props.onSubmit(v)}
          submitting={props.submitting}
          onDraftChange={props.onDraftChange.education}
        />
      );
    case 'contact':
      return (
        <ContactEditor
          initial={draftFor?.contact}
          onDirty={props.onDirty}
          onSubmit={(v) => props.onSubmit(v)}
          submitting={props.submitting}
          onDraftChange={props.onDraftChange.contact}
        />
      );
  }
}

function SaveIndicator({ state }: { state: SaveState }) {
  if (state === 'saving') {
    return (
      <Badge tone="soft" leadingDot>
        <Cloud size={12} /> Saving
      </Badge>
    );
  }
  if (state === 'dirty') {
    return (
      <Badge tone="ink" leadingDot>
        Unsaved
      </Badge>
    );
  }
  return null;
}
